import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MapIcon from "@mui/icons-material/Map";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CloseIcon from "@mui/icons-material/Close";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { getSocket } from "../../services/socketService";
import { tokens } from "../../theme";
import RobotPicture from "../../components/ui/RobotPicture";
import { StatusChip, Chip, pageContainerSx } from "../../components/ui";
import InitialPositionModal from "./InitialPositionModal";
import RobotOperationsTab from "./RobotOperationsTab";
import LightController from "../../components/delivery/LightController";
import LiveRobotPositionViewer from "../../utils/LiveRobotPositionViewer";
import { RobotTelemetryPanel } from "../../features/robotTelemetry";
import { radToDisplayDeg } from "../../utils/mapCoords";

const InfoRow = ({ label, value }) => (
  <Stack
    direction="row"
    gap={2}
    sx={{ p: "11px 16px", borderBottom: `1px solid ${tokens.border}` }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        color: tokens.text3,
        // Narrower once the card splits into two columns, so the value still
        // has room; the full 150 is kept in the stacked single-column layout.
        width: { xs: 150, md: 128 },
        flexShrink: 0,
        pt: "1px",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 13,
        color: tokens.text,
        fontWeight: 500,
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Stack>
);

InfoRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
};

const TABS = ["details", "operations", "activity", "map"];

const RobotDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [robot, setRobot] = useState(null);
  const [loading, setLoading] = useState(true);
  // Live socket connectivity for THIS robot, used to resolve the status chip below.
  const [liveConnected, setLiveConnected] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showLightDialog, setShowLightDialog] = useState(false);
  const [toast, setToast] = useState(null);

  // Tab lives in the URL so a refresh or a shared link reopens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = TABS.includes(tabParam) ? tabParam : "details";
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "details") params.delete("tab");
    else params.set("tab", next);
    // replace, not push: tab switches shouldn't stack up in the back button.
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const fetchRobot = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setRobot(await res.json());
        else navigate("/robots");
      } catch {
        navigate("/robots");
      } finally {
        setLoading(false);
      }
    };
    fetchRobot();
  }, [id, navigate]);

  // Live connectivity for the status chip: the stored `status` is only rewritten
  // on a debounced connect/disconnect transition, so it can read stale otherwise.
  // Subscribes by `serialID` (rooms are scoped by serialID, not the route's Mongo _id).
  useEffect(() => {
    const serialID = robot?.serialID;
    if (!serialID) return undefined;
    const key = String(serialID);

    const socket = getSocket();

    // Re-emitted on every connect: room membership is per-connection and is lost on
    // reconnect, and a mount-time emit is dropped if the socket is not up yet.
    const joinRoom = () => socket.emit("subscribe-robot", key);
    socket.on("connect", joinRoom);
    if (socket.connected) joinRoom();

    const matches = (msg) => {
      const serial = msg?.serialID ?? msg?.robotId ?? msg?.data?.serialID;
      return serial != null && String(serial) === key;
    };
    const markConnected = (msg) => {
      if (matches(msg)) setLiveConnected(true);
    };
    const markDisconnected = (msg) => {
      if (matches(msg)) setLiveConnected(false);
    };

    socket.on("ACTION_CURRENT_POSE", markConnected);
    socket.on("nav_progress", markConnected);
    socket.on("location_data", markConnected);
    socket.on("BATTERY_STATUS_UPDATE", markConnected);
    socket.on("robot-status", markConnected);
    socket.on("SENSOR_STATUS_UPDATE", markConnected);
    socket.on("robot_connected", markConnected);
    socket.on("robot_disconnected", markDisconnected);

    return () => {
      // The socket is the app-wide shared instance (services/socketService), so only
      // the listeners and the room are released here — never the connection itself.
      socket.emit("unsubscribe-robot", key);
      socket.off("connect", joinRoom);
      socket.off("ACTION_CURRENT_POSE", markConnected);
      socket.off("nav_progress", markConnected);
      socket.off("location_data", markConnected);
      socket.off("BATTERY_STATUS_UPDATE", markConnected);
      socket.off("robot-status", markConnected);
      socket.off("SENSOR_STATUS_UPDATE", markConnected);
      socket.off("robot_connected", markConnected);
      socket.off("robot_disconnected", markDisconnected);
    };
  }, [robot?.serialID]);

  // Reflect a saved pose locally — the PATCH response carries the stored value,
  // so there's no need to refetch the whole robot.
  const handlePositionSaved = (res) => {
    setRobot((r) => (r ? { ...r, initialPosition: res?.initialPosition ?? null } : r));
    if (!res?.initialPosition) {
      setToast({ severity: "success", text: "Initial position cleared." });
      return;
    }
    setToast(
      res.pushedToRobot
        ? { severity: "success", text: "Initial position saved and sent to the robot." }
        : {
            severity: "info",
            text: "Initial position saved. The robot is offline — it will apply on next connect.",
          }
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: tokens.accent }} />
      </Box>
    );
  }
  if (!robot) return null;

  // Stored status with live connectivity layered over it — same rule as MyRobots'
  // resolveRobotStatus: 'error'/'unassigned' are never overridden by connectivity.
  const resolvedStatus =
    liveConnected && robot.status !== "error" && robot.status !== "unassigned"
      ? "active"
      : robot.status;

  const mapId = typeof robot.assignedMap === "object" ? robot.assignedMap?._id : robot.assignedMap;
  const ip = robot.initialPosition;
  // A pose captured on a different map is in the wrong coordinate frame, so say
  // so rather than printing numbers that mean nothing on the current map.
  const staleFrame = ip && mapId && String(ip.mapId) !== String(mapId);

  // Same staleness guard initialPosition already uses above: a last-known
  // pose recorded against a DIFFERENT map (the robot was reassigned since) is
  // meaningless on THIS map, so it's withheld from LiveRobotPositionViewer
  // rather than shown on the wrong floor plan.
  const lkp = robot.lastKnownPosition;
  const lkpMapId = lkp && (typeof lkp.mapId === "object" ? lkp.mapId?._id : lkp.mapId);
  const lkpMatchesCurrentMap = !!lkp && !!mapId && String(lkpMapId) === String(mapId);
  const scopedLastKnownPosition = lkpMatchesCurrentMap ? lkp : null;

  const initialPositionLabel = (() => {
    if (!ip) return "Not set";
    if (staleFrame) return "Set on a different map — re-mark it";
    return `x ${ip.x.toFixed(2)} m,  y ${ip.y.toFixed(2)} m,  ${radToDisplayDeg(ip.yaw)}°`;
  })();

  const fmtDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : undefined;

  // Relative age alone goes vague fast — "14d ago" is unactionable — so the
  // exact timestamp rides along in parentheses.
  const fmtLastRun = (value) => {
    if (!value) return undefined;
    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return undefined;

    const exact = then.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Clamp negatives: a robot clock running ahead of the server must not
    // render "in 3 minutes" on a run that has already finished.
    const diffMs = Math.max(0, Date.now() - then.getTime());
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return `Just now (${exact})`;
    if (minutes < 60) return `${minutes}m ago (${exact})`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago (${exact})`;
    return `${Math.floor(hours / 24)}d ago (${exact})`;
  };

  // Split across two columns on wide screens. Identity/hardware on the left,
  // placement and dates on the right — so neither column is all mono serials.
  const infoLeft = [
    ["Robot Name", robot.name],
    ["Serial Number", robot.serialNumber],
    ["Serial ID", robot.serialID],
    ["MAC Address", robot.macaddress],
    [
      "Robot Type",
      robot.roverType
        ? robot.roverType.charAt(0).toUpperCase() + robot.roverType.slice(1)
        : robot.type,
    ],
  ];
  const infoRight = [
    ["Map", robot.assignedMap?.name || "Not assigned"],
    ["Initial Position", initialPositionLabel],
    ["Last Run", fmtLastRun(robot.lastRunAt)],
    ["Assigned Date", fmtDate(robot.assignedAt)],
    ["Added to System", fmtDate(robot.createdAt)],
  ];

  return (
    <Box sx={pageContainerSx()}>
      {/* Breadcrumb rather than a bare back button: the page is now reachable
          from the robots list AND from a shared ?tab= link, so it should say
          where it sits, not just offer a way out. */}
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/robots")}
          size="small"
          sx={{
            color: tokens.text2,
            minWidth: 0,
            "&:hover": { color: tokens.text, background: tokens.surface2 },
          }}
        >
          My Robots
        </Button>
        <Typography sx={{ fontSize: 13, color: tokens.text3 }}>›</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
          {robot.name}
        </Typography>
      </Stack>

      {/* Bordered, subtly-shadowed identity card — same chrome as
          botriq-admin-fe's Robot Details identity bar, so the two portals'
          detail pages read as one system. */}
      <Card
        elevation={0}
        sx={{
          p: 2.25,
          mb: 1.75,
          border: `1px solid ${tokens.border}`,
          borderRadius: "12px",
          boxShadow: tokens.shadow,
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <RobotPicture
            robotId={robot._id}
            botTypeId={robot.deviceType?._id}
            name={robot.name}
            size={56}
            radius={16}
          />
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: tokens.text }}>
                {robot.name}
              </Typography>
              {/* Same StatusChip botriq-admin-fe's Fleet/My Robots cards render,
                  so a robot's status reads identically everywhere in the portal. */}
              <StatusChip status={resolvedStatus} charging={!!robot.charging} />
              {robot.disabled?.isDisabled && <Chip label="Disabled" tone="danger" />}
            </Stack>
            {robot.roverType && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: tokens.text3,
                  fontFamily: "'JetBrains Mono',monospace",
                  mt: 0.25,
                  textTransform: "capitalize",
                }}
              >
                {robot.roverType} · {robot.serialNumber}
              </Typography>
            )}
          </Box>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MyLocationIcon />}
              disabled={!mapId}
              title={mapId ? undefined : "Assign a map to this robot first"}
              onClick={() => setShowPositionModal(true)}
              sx={{ borderColor: tokens.border, color: tokens.text }}
            >
              {ip ? "Edit start position" : "Set start position"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MapIcon />}
              onClick={() => navigate("/venue")}
              sx={{ borderColor: tokens.border, color: tokens.text }}
            >
              View venue
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        sx={{
          minHeight: 0,
          mb: 1.75,
          borderBottom: `1px solid ${tokens.border}`,
          "& .MuiTab-root": {
            minHeight: 0,
            py: 1.25,
            px: 1.75,
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            color: tokens.text3,
            "&.Mui-selected": { color: tokens.accent },
          },
        }}
      >
        <Tab
          value="details"
          label="Details"
          icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
        {/* Operations — everything you DO to this robot (dispatch, patrol), as
            opposed to Details' read-only information. Mirrors the admin portal's
            Robot Details ▸ Operations tab. */}
        <Tab
          value="operations"
          label="Operations"
          icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
        {/* Activity — what the robot did, day by day, from its finished runs.
            The admin portal's Telemetry tab, without the navigation-health
            counters that only staff act on. */}
        <Tab
          value="activity"
          label="Activity"
          icon={<BarChartOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
        <Tab
          value="map"
          label="Map"
          icon={<MapIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
      </Tabs>

      {tab === "details" && (
        <Card>
          <Box sx={{ p: "14px 16px 4px" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
              Robot information
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              // The divider only makes sense once the columns sit side by side;
              // stacked, it would cut across the middle of the list.
              "& > :first-of-type": {
                borderRight: { md: `1px solid ${tokens.border}` },
              },
            }}
          >
            <Box>
              {infoLeft.map(([label, value]) => (
                <InfoRow key={label} label={label} value={value} />
              ))}
            </Box>
            <Box>
              {infoRight.map(([label, value]) => (
                <InfoRow key={label} label={label} value={value} />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              m: 2,
              mt: 1.75,
              p: 1.4,
              borderRadius: "10px",
              background: tokens.warningSoft,
              border: `1px solid ${tokens.warning}30`,
            }}
          >
            <Typography sx={{ fontSize: 11.5, color: tokens.warning, fontWeight: 500 }}>
              The initial position is the only setting you can change here. All other robot
              configuration is managed by administrators.
            </Typography>
          </Box>
        </Card>
      )}

      {tab === "operations" && (
        <RobotOperationsTab
          robot={robot}
          // Both flows resolve this via `location.state.presetRobotId` on mount
          // and fall back to their existing selection method (a picker/dropdown)
          // when it's absent, so this preset is additive, not a required contract.
          onDispatch={() => navigate("/robot-tracker", { state: { presetRobotId: robot._id } })}
          onPatrol={() => navigate("/patrol", { state: { presetRobotId: robot._id } })}
          onOpenLightDialog={() => setShowLightDialog(true)}
        />
      )}

      {tab === "activity" && <RobotTelemetryPanel robotId={robot._id} heading="Activity" />}

      {tab === "map" && (
        <Card
          sx={{
            overflow: "hidden",
            height: { xs: "calc(100vh - 320px)", md: "calc(100vh - 280px)" },
            minHeight: 420,
          }}
        >
          {/* The viewer renders its own "No Map Selected" state, so an unassigned
              robot needs no branch here. */}
          <LiveRobotPositionViewer
            mapId={mapId}
            selectedSerialID={robot.serialID ?? null}
            isRobotConnected={liveConnected}
            lastKnownPosition={scopedLastKnownPosition}
          />
        </Card>
      )}

      {showPositionModal && (
        <InitialPositionModal
          open
          robot={robot}
          onClose={() => setShowPositionModal(false)}
          onSaved={handlePositionSaved}
        />
      )}

      {/* `robot` passed explicitly so it never falls back to a different cached robot. */}
      <Dialog
        open={showLightDialog}
        onClose={() => setShowLightDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 15, fontWeight: 700 }}
        >
          <LightbulbIcon sx={{ fontSize: 19, color: tokens.accent }} />
          Light Control — {robot.name}
          <IconButton
            onClick={() => setShowLightDialog(false)}
            size="small"
            aria-label="Close"
            sx={{ ml: "auto", color: tokens.text3, "&:hover": { color: tokens.text } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 2.5 }}>
          {showLightDialog && <LightController robot={robot} />}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity || "success"}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ fontSize: 13 }}
        >
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RobotDetails;
